import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { useAdminClient } from '../context/AdminClientContext';
import { useAuth } from '../context/AuthContext';
import { useSimulations, usePopulations, useStimuli } from '../hooks/useClientScopedData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Activity, Plus, Play, Trash2, Clock, Users, Zap, Loader2, CheckCircle2, AlertCircle,
  ChevronDown, RotateCcw, BarChart3, Eye, Layers, PlayCircle, Sparkles, Search, Star,
  Copy, X, RefreshCw, ArrowUpDown, Filter, Check, Square, CheckSquare, Network, Globe
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

// Extracted components
import { SuccessCelebration, RunningSimulationsPanel, CreateSimulationDialog, BatchCreateDialog } from '../components/simulations';

const SIMULATION_ICON = "https://static.prod-images.emergentagent.com/jobs/cbfc5637-1130-4ba7-aa02-4a90e91893b5/images/588022ced86db7203d3a5ea42d1a54b209b5d40bbd38f6ef4f258903f0e8d2cd.png";

const STATUS_SORT_ORDER = { running: 0, pending: 1, completed: 2, failed: 3 };

const SimulationSkeleton = () => (
  <div className="p-4 animate-pulse">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 bg-slate-200 rounded-lg" />
      <div className="flex-1 space-y-3">
        <div className="flex items-center gap-3"><div className="h-5 w-48 bg-slate-200 rounded" /><div className="h-5 w-20 bg-slate-200 rounded-full" /></div>
        <div className="flex items-center gap-4"><div className="h-4 w-32 bg-slate-100 rounded" /><div className="h-4 w-32 bg-slate-100 rounded" /><div className="h-4 w-24 bg-slate-100 rounded" /></div>
      </div>
      <div className="flex gap-2"><div className="h-8 w-20 bg-slate-200 rounded" /><div className="h-8 w-8 bg-slate-200 rounded" /></div>
    </div>
  </div>
);

const EmptyState = ({ hasRequirements, onCreateClick }) => (
  <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-12 text-center">
    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-100 flex items-center justify-center">
      <Sparkles className="w-10 h-10 text-blue-500" />
    </div>
    <h3 className="text-xl font-semibold text-slate-900">No simulations yet</h3>
    <p className="text-slate-500 mt-2 max-w-md mx-auto">Create your first simulation to model how your synthetic population would respond to different stimuli</p>
    {hasRequirements ? (
      <Button className="mt-6 bg-primary hover:bg-primary/90" onClick={onCreateClick}><Plus className="w-4 h-4 mr-2" />Create Your First Simulation</Button>
    ) : (
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link to="/populations"><Button variant="outline" className="gap-2"><Users className="w-4 h-4" />Upload Population</Button></Link>
        <Link to="/stimuli"><Button variant="outline" className="gap-2"><Zap className="w-4 h-4" />Create Stimulus</Button></Link>
      </div>
    )}
  </div>
);

export default function Simulations() {
  const { user } = useAuth();
  const { selectedClientId, activeClient } = useAdminClient();
  const { data: simulations, loading: simsLoading, refetch: refetchSimulations, silentRefetch: silentRefetchSimulations } = useSimulations();
  const { data: populations, loading: popsLoading } = usePopulations();
  const { data: stimuli, loading: stimsLoading } = useStimuli();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [simulationToDelete, setSimulationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [runningIds, setRunningIds] = useState(new Set());
  // B1: removed full-screen blocking spinner state (showStartAnimation, pendingRunId).
  // Running simulations are displayed via the existing RunningSimulationsPanel.
  const [searchQuery, setSearchQuery] = useState('');
  const [showCelebration, setShowCelebration] = useState(false);
  const [completedSimName, setCompletedSimName] = useState('');
  const [cloning, setCloning] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);

  const loading = simsLoading || popsLoading || stimsLoading;

  // Track running simulations
  useEffect(() => {
    if (simulations) setRunningIds(new Set(simulations.filter(s => s.status === 'running').map(s => s.id)));
  }, [simulations]);

  // Poll for running simulation updates
  useEffect(() => {
    if (runningIds.size === 0) return;
    const interval = setInterval(() => {
      silentRefetchSimulations();
      if (simulations) {
        simulations.forEach(sim => {
          if (runningIds.has(sim.id) && (sim.status === 'completed' || sim.status === 'completed_with_warning' || sim.status === 'completed_with_warnings' || sim.status === 'failed')) {
            setRunningIds(r => { const n = new Set(r); n.delete(sim.id); return n; });
            if (sim.status === 'completed' || sim.status === 'completed_with_warning' || sim.status === 'completed_with_warnings') {
              setCompletedSimName(sim.name); setShowCelebration(true);
              toast.success(`Simulation "${sim.name}" completed!`);
            }
          }
        });
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [runningIds, simulations, silentRefetchSimulations]);

  const filteredSimulations = useMemo(() => {
    return (simulations || [])
      .filter(sim => sim.name.toLowerCase().includes(searchQuery.toLowerCase()) && (statusFilter === 'all' || sim.status === statusFilter))
      .sort((a, b) => {
        if (a.starred && !b.starred) return -1;
        if (!a.starred && b.starred) return 1;
        let cmp = 0;
        if (sortBy === 'date') cmp = new Date(a.created_at) - new Date(b.created_at);
        else if (sortBy === 'name') cmp = a.name.localeCompare(b.name);
        else if (sortBy === 'status') cmp = (STATUS_SORT_ORDER[a.status] || 99) - (STATUS_SORT_ORDER[b.status] || 99);
        return sortOrder === 'asc' ? cmp : -cmp;
      });
  }, [simulations, searchQuery, statusFilter, sortBy, sortOrder]);

  const pendingSimulations = simulations?.filter(s => s.status === 'pending') || [];

  const handleRefresh = async () => { setIsRefreshing(true); await refetchSimulations(); setTimeout(() => setIsRefreshing(false), 500); toast.success('Refreshed'); };
  const toggleSelectAll = () => { setSelectedIds(selectedIds.size === filteredSimulations.length ? new Set() : new Set(filteredSimulations.map(s => s.id))); };
  const toggleSelectOne = (id, e) => { e.stopPropagation(); const n = new Set(selectedIds); n.has(id) ? n.delete(id) : n.add(id); setSelectedIds(n); };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} simulation(s)? This cannot be undone.`)) return;
    try { await Promise.all([...selectedIds].map(id => api.delete(`/simulations/${id}`))); toast.success(`Deleted ${selectedIds.size} simulation(s)`); setSelectedIds(new Set()); refetchSimulations(); }
    catch { toast.error('Failed to delete some simulations'); }
  };

  const handleBulkRunSelected = async () => {
    const pending = [...selectedIds].filter(id => simulations.find(s => s.id === id)?.status === 'pending');
    if (pending.length === 0) { toast.error('No pending simulations selected'); return; }
    handleBatchRun(pending); setSelectedIds(new Set());
  };

  const handleClone = async (simulationId) => {
    setCloning(simulationId);
    try { await api.post(`/simulations/${simulationId}/clone`); toast.success('Simulation cloned successfully'); refetchSimulations(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to clone simulation'); }
    finally { setCloning(null); }
  };

  const handleToggleStar = async (simulationId, e) => {
    e.stopPropagation();
    try { await api.post(`/simulations/${simulationId}/star`); silentRefetchSimulations(); }
    catch { toast.error('Failed to update star status'); }
  };

  const handleBatchRun = async (simulationIds) => {
    setBatchRunning(true);
    try { const r = await api.post('/simulations/batch/run', { simulation_ids: simulationIds }); setRunningIds(prev => new Set([...prev, ...simulationIds])); toast.success(`Queued ${r.data.queued_ids.length} simulations · running in background`, { description: 'You\'ll be notified as each completes.' }); refetchSimulations(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to start batch simulations'); }
    finally { setBatchRunning(false); }
  };

  const handleRun = async (id) => {
    setRunningIds(r => new Set([...r, id]));
    toast.success('Simulation queued', { description: 'Running in the background — refresh status appears below.' });
    try {
      api.post(`/simulations/${id}/run`).then(() => { refetchSimulations(); }).catch((error) => {
        toast.error(error.response?.data?.detail || 'Failed to run simulation');
        setRunningIds(r => { const n = new Set(r); n.delete(id); return n; }); refetchSimulations();
      });
    } catch { toast.error('Failed to start simulation'); }
  };

  const handleStop = async (id) => {
    try { await api.post(`/simulations/${id}/stop`); toast.success('Simulation stop requested'); setRunningIds(r => { const n = new Set(r); n.delete(id); return n; }); refetchSimulations(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to stop simulation'); }
  };

  const handleReset = async (id) => {
    try { await api.post(`/simulations/${id}/reset`); toast.success('Simulation reset to pending'); refetchSimulations(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to reset simulation'); }
  };

  const handleDelete = async () => {
    if (!simulationToDelete) return;
    setDeleting(true);
    try { await api.delete(`/simulations/${simulationToDelete.id}`); toast.success('Simulation deleted'); setDeleteDialogOpen(false); setSimulationToDelete(null); refetchSimulations(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete simulation'); }
    finally { setDeleting(false); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-800';
      case 'completed_with_warning': case 'completed_with_warnings': return 'bg-amber-100 text-amber-800';
      case 'running': return 'bg-blue-100 text-blue-800';
      case 'queued': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div data-testid="simulations-page" className="space-y-6">
        <div className="flex items-start justify-between">
          <div><div className="h-8 w-48 bg-slate-200 rounded animate-pulse" /><div className="h-4 w-64 bg-slate-100 rounded mt-2 animate-pulse" /></div>
          <div className="flex gap-2"><div className="h-9 w-24 bg-slate-200 rounded animate-pulse" /><div className="h-9 w-32 bg-slate-200 rounded animate-pulse" /></div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">{[1, 2, 3, 4].map(i => <SimulationSkeleton key={i} />)}</div>
      </div>
    );
  }

  return (
    <div data-testid="simulations-page" className="space-y-6">
      <SuccessCelebration isVisible={showCelebration} simulationName={completedSimName} onClose={() => setShowCelebration(false)} />
      
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">Simulations</h1>
          <p className="text-gray-500 dark:text-slate-400 mt-1 text-sm">Run AI-powered population behavior simulations</p>
        </div>
        <div className="flex items-center gap-2">
          {pendingSimulations.length > 1 && (
            <Button variant="outline" onClick={() => handleBatchRun(pendingSimulations.map(s => s.id))} disabled={batchRunning} data-testid="run-all-pending-btn">
              {batchRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}Run All ({pendingSimulations.length})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setBatchDialogOpen(true)} disabled={populations.length === 0 || stimuli.length < 2} className="h-9" data-testid="batch-create-btn">
            <Layers className="w-4 h-4 mr-1.5" />Batch
          </Button>
          <Button size="sm" className="bg-primary hover:bg-primary/90 h-9" onClick={() => setDialogOpen(true)} disabled={populations.length === 0 || stimuli.length === 0} data-testid="create-simulation-btn">
            <Plus className="w-4 h-4 mr-1.5" />New Simulation
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      {simulations.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input type="text" placeholder="Search simulations..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-10" data-testid="search-simulations" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
          </div>
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            {['all', 'completed', 'running', 'pending', 'failed'].map(status => (
              <button key={status} onClick={() => setStatusFilter(status)} className={cn("px-3 py-1.5 text-xs font-medium rounded-md transition-all", statusFilter === status ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white")}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
          <Select value={`${sortBy}-${sortOrder}`} onValueChange={(v) => { const [f, o] = v.split('-'); setSortBy(f); setSortOrder(o); }}>
            <SelectTrigger className="w-40 h-10"><ArrowUpDown className="w-4 h-4 mr-2" /><SelectValue placeholder="Sort by" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="date-desc">Newest first</SelectItem>
              <SelectItem value="date-asc">Oldest first</SelectItem>
              <SelectItem value="name-asc">Name A-Z</SelectItem>
              <SelectItem value="name-desc">Name Z-A</SelectItem>
              <SelectItem value="status-asc">Status</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={handleRefresh} disabled={isRefreshing} className="h-10 w-10" data-testid="refresh-simulations">
            <RefreshCw className={cn("w-4 h-4", isRefreshing && "animate-spin")} />
          </Button>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-lg animate-fade-in">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-300">{selectedIds.size} selected</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={handleBulkRunSelected} className="text-blue-700 border-blue-300 hover:bg-blue-100"><Play className="w-4 h-4 mr-1" />Run Selected</Button>
          <Button size="sm" variant="outline" onClick={handleBulkDelete} className="text-red-700 border-red-300 hover:bg-red-100"><Trash2 className="w-4 h-4 mr-1" />Delete Selected</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}><X className="w-4 h-4" /></Button>
        </div>
      )}

      {/* Requirements Warning */}
      {(populations.length === 0 || stimuli.length === 0) && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Setup Required</p>
            <p className="text-sm text-amber-700 mt-1">
              To run simulations, you need:
              {populations.length === 0 && <Link to="/populations" className="underline ml-1 hover:text-amber-900">Upload a population</Link>}
              {populations.length === 0 && stimuli.length === 0 && ' and '}
              {stimuli.length === 0 && <Link to="/stimuli" className="underline ml-1 hover:text-amber-900">Create a stimulus</Link>}
            </p>
          </div>
        </div>
      )}

      <RunningSimulationsPanel simulations={simulations} onStop={handleStop} />

      {/* Simulations List */}
      {filteredSimulations.length > 0 ? (
        <div className="bg-white dark:bg-card rounded-xl shadow-sm border border-slate-200 dark:border-border overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 dark:bg-muted border-b border-slate-200 dark:border-border">
            <button onClick={toggleSelectAll} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-secondary transition-colors">
              {selectedIds.size === filteredSimulations.length && filteredSimulations.length > 0 ? <CheckSquare className="w-5 h-5 text-blue-600" />
                : selectedIds.size > 0 ? <div className="w-5 h-5 border-2 border-blue-600 rounded flex items-center justify-center"><div className="w-2 h-2 bg-blue-600 rounded-sm" /></div>
                : <Square className="w-5 h-5 text-slate-400" />}
            </button>
            <span className="text-xs text-slate-500 dark:text-muted-foreground">{filteredSimulations.length} simulation{filteredSimulations.length !== 1 ? 's' : ''}{statusFilter !== 'all' && ` (${statusFilter})`}</span>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-border">
            {filteredSimulations.map((sim, index) => (
              <div key={sim.id} className={cn("p-4 hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-all duration-300 relative group",
                sim.status === 'running' && "bg-blue-50/30 dark:bg-blue-500/5",
                (sim.status === 'completed' || sim.status === 'completed_with_warning' || sim.status === 'completed_with_warnings') && "hover:bg-emerald-50/30 dark:hover:bg-emerald-500/5",
                sim.starred && "bg-amber-50/20 dark:bg-amber-500/5", selectedIds.has(sim.id) && "bg-blue-50/50 dark:bg-blue-500/10"
              )} style={{ animationDelay: `${index * 50}ms` }} data-testid={`simulation-item-${sim.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-4 flex-1 min-w-0">
                    <button onClick={(e) => toggleSelectOne(sim.id, e)} className="mt-1 p-1 rounded hover:bg-slate-200 dark:hover:bg-secondary transition-colors">
                      {selectedIds.has(sim.id) ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-slate-300 dark:text-slate-600 group-hover:text-slate-400" />}
                    </button>
                    <button onClick={(e) => handleToggleStar(sim.id, e)} className={cn("mt-1 p-1 rounded transition-all", sim.starred ? "text-amber-500 hover:text-amber-600" : "text-slate-300 dark:text-slate-600 hover:text-amber-400 opacity-0 group-hover:opacity-100")} data-testid={`star-simulation-${sim.id}`}>
                      <Star className={cn("w-5 h-5", sim.starred && "fill-current")} />
                    </button>
                    <div className={cn("w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden relative",
                      sim.status === 'pending' && "bg-slate-100 dark:bg-slate-800", sim.status === 'running' && "bg-blue-100 dark:bg-blue-500/15",
                      (sim.status === 'completed' || sim.status === 'completed_with_warning' || sim.status === 'completed_with_warnings') && "bg-emerald-100 dark:bg-emerald-500/15", sim.status === 'failed' && "bg-red-100 dark:bg-red-500/15"
                    )}>
                      {sim.status === 'running' && <div className="absolute inset-0 rounded-lg status-live-ring" />}
                      {sim.status === 'running' ? <Activity className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-pulse" />
                        : (sim.status === 'completed' || sim.status === 'completed_with_warning' || sim.status === 'completed_with_warnings') ? <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                        : sim.status === 'failed' ? <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                        : <img src={SIMULATION_ICON} alt="" className="w-8 h-8 object-contain opacity-70" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium text-gray-900 dark:text-white truncate">{sim.name}</h3>
                        <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium capitalize transition-all", getStatusColor(sim.status), sim.status === 'running' && "animate-pulse")}>
                          {sim.status === 'running' && <Loader2 className="w-3 h-3 inline mr-1 animate-spin" />}{sim.status}
                        </span>
                        {sim.config?.use_pre && <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30">PRE</Badge>}
                        {sim.social_enabled && <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30" data-testid="social-badge"><Network className="w-3 h-3 mr-1" />SIDE</Badge>}
                        {sim.module_type === 'market_lab' && <Badge variant="outline" className="text-xs bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30">MarketLab</Badge>}
                        {sim.module_type === 'pharma_lab' && <Badge variant="outline" className="text-xs bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30">PharmaLab</Badge>}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-slate-400">
                        <span className="flex items-center gap-1.5"><Users className="w-4 h-4" />{populations.find(p => p.id === sim.population_id)?.name || 'Unknown'}</span>
                        <span className="flex items-center gap-1.5"><Zap className="w-4 h-4" />{stimuli.find(s => s.id === sim.stimulus_id)?.name || 'Unknown'}</span>
                        <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{new Date(sim.created_at).toLocaleDateString()}</span>
                      </div>
                      {sim.status === 'running' && (
                        <div className="mt-3 max-w-md">
                          <Progress value={sim.progress} className="h-2" />
                          <div className="flex items-center justify-between mt-1"><p className="text-xs text-blue-600 dark:text-blue-400 font-medium">{sim.progress}% complete</p><p className="text-xs text-gray-400 dark:text-slate-500">Processing responses...</p></div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {sim.status === 'pending' && <Button size="sm" onClick={() => handleRun(sim.id)} className="bg-primary hover:bg-primary/90" data-testid={`run-simulation-${sim.id}`}><Play className="w-4 h-4 mr-1" />Run</Button>}
                    {sim.status === 'running' && <Button size="sm" variant="destructive" onClick={() => handleStop(sim.id)} data-testid={`stop-simulation-${sim.id}`}><RotateCcw className="w-4 h-4 mr-1" />Stop</Button>}
                    {(sim.status === 'failed' || sim.status === 'stopped') && <Button size="sm" variant="outline" onClick={() => handleReset(sim.id)} data-testid={`reset-simulation-${sim.id}`}><RotateCcw className="w-4 h-4 mr-1" />Reset</Button>}
                    {(sim.status === 'completed' || sim.status === 'completed_with_warning' || sim.status === 'completed_with_warnings') && (
                      <Button variant="outline" size="sm" asChild><Link to={`/analysis?simulation=${sim.id}`} data-testid={`view-results-${sim.id}`}><BarChart3 className="w-4 h-4 mr-1" />Results</Link></Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleClone(sim.id)} disabled={cloning === sim.id} className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10" data-testid={`clone-simulation-${sim.id}`}>
                      {cloning === sim.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setSimulationToDelete(sim); setDeleteDialogOpen(true); }} className="text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10" disabled={sim.status === 'running'} data-testid={`delete-simulation-${sim.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : searchQuery || statusFilter !== 'all' ? (
        <div className="bg-white dark:bg-card rounded-xl border border-slate-200 dark:border-border p-12 text-center">
          <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900">No results found</h3>
          <p className="text-slate-500 text-sm mt-1">Try a different search term</p>
          <Button variant="outline" className="mt-4" onClick={() => setSearchQuery('')}>Clear Search</Button>
        </div>
      ) : (
        <EmptyState hasRequirements={populations.length > 0 && stimuli.length > 0} onCreateClick={() => setDialogOpen(true)} />
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Simulation</DialogTitle>
            <DialogDescription>Are you sure you want to delete &quot;{simulationToDelete?.name}&quot;? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
              {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Simulation Dialog */}
      <CreateSimulationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        populations={populations}
        stimuli={stimuli}
        activeClient={activeClient}
        user={user}
        selectedClientId={selectedClientId}
        onCreated={() => { toast.success('Simulation created'); refetchSimulations(); }}
        api={api}
      />

      {/* Batch Create Dialog */}
      <BatchCreateDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        populations={populations}
        stimuli={stimuli}
        user={user}
        selectedClientId={selectedClientId}
        onCreated={refetchSimulations}
        api={api}
      />
    </div>
  );
}
